/**
 * `infras-file:<uuid>` — how a markdown body points at an uploaded attachment:
 * `![screenshot.png](infras-file:6f1c…)`. Pure (no React / Apollo) so
 * `scripts/check-rich-editor-md.mjs` can exercise it. Mirrors
 * packages/inventory-core/src/files/refs.ts, which the api uses for email/Slack text.
 */
import { defaultUrlTransform } from 'react-markdown';

export const FILE_REF_SCHEME = 'infras-file:';
const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const UUID_RE = new RegExp(`^${UUID}$`);
const refRe = () => new RegExp(`!?\\[([^\\]\\n]*)\\]\\(\\s*infras-file:(${UUID})(?:\\s+"[^"]*")?\\s*\\)`, 'g');

/** The uuid in `infras-file:<uuid>` (lower-cased), or null for any other src. */
export function fileIdOf(src: string | null | undefined): string | null {
  if (!src || !src.startsWith(FILE_REF_SCHEME)) return null;
  const id = src.slice(FILE_REF_SCHEME.length).trim();
  return UUID_RE.test(id) ? id.toLowerCase() : null;
}

/** Same shape the api writes into the upload ticket's `ref` (brackets would end the alt). */
export const fileRef = (id: string, name: string) => `![${name.replace(/[[\]]/g, '_')}](${FILE_REF_SCHEME}${id})`;

/** Every reference in a markdown body, in order. */
export function parseFileRefs(md: string | null | undefined): { id: string; name: string }[] {
  return [...String(md ?? '').matchAll(refRe())].map((m) => ({ id: m[2].toLowerCase(), name: m[1] }));
}

/** Each reference → `[attachment: name]`, for plain-text surfaces. */
export function fileRefsToText(md: string | null | undefined): string {
  return String(md ?? '').replace(refRe(), (_all, name: string) => `[attachment: ${name.trim() || 'file'}]`);
}

/**
 * react-markdown's default transform blanks any scheme it does not know; `infras-file:` is
 * ours (resolved to a signed link by <FileEmbed>), everything else keeps the safe default.
 */
export const markdownUrlTransform = (url: string): string =>
  url.startsWith(FILE_REF_SCHEME) ? url : defaultUrlTransform(url);
